/**
 * Compliance Tracking Page
 * BuilderBel Construction Project Management System
 */

import {
  BadgeCheck,
  Building2,
  Calendar,
  Download,
  FileWarning,
  Pencil,
  Plus,
  Search,
  Shield,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input, SearchableDropdown, TextArea } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { ComplianceDrawer } from '../components/compliance';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useDeleteComplianceItemMutation,
  useGetComplianceItemsQuery,
  useGetComplianceStatsQuery,
  useVerifyComplianceItemMutation,
} from '../services/complianceApi';
import type {
  ComplianceItem,
  ComplianceItemStatus,
  ComplianceItemType,
} from '../types/compliance';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'applied', label: 'Applied' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'issued', label: 'Issued' },
  { value: 'expired', label: 'Expired' },
  { value: 'closed', label: 'Closed' },
  { value: 'valid', label: 'Valid' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'permit', label: 'Permit' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'certification', label: 'Certification' },
  { value: 'requirement', label: 'Requirement' },
];

const EXPIRING_OPTIONS = [
  { value: '', label: 'Any Expiration' },
  { value: '30', label: 'Expiring in 30 days' },
  { value: '60', label: 'Expiring in 60 days' },
  { value: '90', label: 'Expiring in 90 days' },
];

// ============================================================================
// HELPERS
// ============================================================================

const getStatusVariant = (
  status: ComplianceItemStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'purple' => {
  switch (status) {
    case 'issued':
    case 'valid':
    case 'approved':
      return 'success';
    case 'applied':
    case 'under_review':
      return 'info';
    case 'expiring_soon':
      return 'warning';
    case 'expired':
      return 'error';
    case 'closed':
      return 'neutral';
    case 'not_applied':
    default:
      return 'neutral';
  }
};

const getTypeLabel = (type: ComplianceItemType): string => {
  switch (type) {
    case 'permit':
      return 'Permit';
    case 'insurance':
      return 'Insurance';
    case 'inspection':
      return 'Inspection';
    case 'certification':
      return 'Certification';
    case 'requirement':
      return 'Requirement';
  }
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getDaysUntilExpiration = (dateStr?: string): number | null => {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// ============================================================================
// VERIFY MODAL
// ============================================================================

interface VerifyModalProps {
  item: ComplianceItem;
  onConfirm: (notes: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const VerifyModal: React.FC<VerifyModalProps> = ({ item, onConfirm, onCancel, isLoading }) => {
  const [notes, setNotes] = useState('');

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onCancel} />
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Verify Compliance Item</h2>
            <p className="text-sm text-gray-500 mt-1">{item.name}</p>
          </div>
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Mark this compliance item as verified. You can add optional notes.
            </p>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Verification Notes</label>
              <TextArea
                value={notes}
                onChange={setNotes}
                placeholder="Optional verification notes"
                rows={3}
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(notes)}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:bg-brand-200 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ============================================================================
// PAGE COMPONENT
// ============================================================================

const ComplianceTracking: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // ---- Drawer state ----
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ComplianceItem | null>(null);

  // ---- Verify modal state ----
  const [verifyItem, setVerifyItem] = useState<ComplianceItem | null>(null);

  // ---- Filters ----
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterExpiring, setFilterExpiring] = useState('');
  const [page, setPage] = useState(1);

  const queryParams = useMemo(
    () => ({
      page,
      limit: 20,
      search: search || undefined,
      itemType: (filterType || undefined) as ComplianceItemType | undefined,
      status: (filterStatus || undefined) as ComplianceItemStatus | undefined,
      expiringWithin: filterExpiring ? Number(filterExpiring) : undefined,
      sortBy: 'createdAt',
      sortOrder: 'desc' as const,
    }),
    [page, search, filterType, filterStatus, filterExpiring]
  );

  const {
    data: itemsData,
    isLoading,
    isFetching,
  } = useGetComplianceItemsQuery(
    { projectId: projectId ?? '', params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetComplianceStatsQuery(
    { projectId: projectId ?? '' },
    { skip: !projectId }
  );

  const [deleteItem] = useDeleteComplianceItemMutation();
  const [verifyItemMutation, { isLoading: isVerifying }] = useVerifyComplianceItemMutation();

  const items = itemsData?.data?.complianceItems ?? [];
  const pagination = itemsData?.data?.pagination;
  const stats = statsData?.data;

  // ---- Handlers ----
  const handleAddNew = useCallback(() => {
    setEditingItem(null);
    setIsDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((item: ComplianceItem) => {
    setEditingItem(item);
    setIsDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (item: ComplianceItem) => {
      if (!projectId) return;
      if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
      try {
        await deleteItem({ projectId, itemId: item._id }).unwrap();
        toast.success('Compliance item deleted');
      } catch {
        toast.error('Failed to delete compliance item');
      }
    },
    [projectId, deleteItem, toast]
  );

  const handleVerify = useCallback(async (notes: string) => {
    if (!projectId || !verifyItem) return;
    try {
      await verifyItemMutation({
        projectId,
        itemId: verifyItem._id,
        data: { verificationNotes: notes },
      }).unwrap();
      toast.success('Compliance item verified');
      setVerifyItem(null);
    } catch {
      toast.error('Failed to verify compliance item');
    }
  }, [projectId, verifyItem, verifyItemMutation, toast]);

  const handleDownloadPdf = useCallback(
    async (item: ComplianceItem) => {
      if (!projectId) return;
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          `/api/projects/${projectId}/compliance/${item._id}/pdf`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) throw new Error('PDF generation failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `compliance-${item.itemNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  const getActionItems = useCallback(
    (item: ComplianceItem): ActionMenuItem[] => [
      {
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" />,
        onClick: () => handleEdit(item),
      },
      {
        label: 'Verify',
        icon: <BadgeCheck className="h-4 w-4" />,
        onClick: () => setVerifyItem(item),
      },
      {
        label: 'Download PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: () => handleDownloadPdf(item),
      },
      {
        label: 'Delete',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: () => handleDelete(item),
        variant: 'danger' as const,
      },
    ],
    [handleEdit, handleDelete, handleDownloadPdf]
  );

  // ---- Stats blocks ----
  const statItems = useMemo(
    () => [
      { title: 'Total Items', value: stats?.total ?? 0 },
      { title: 'Valid / Active', value: stats?.valid ?? 0, variant: 'success' as const },
      {
        title: 'Expiring (30d)',
        value: stats?.expiringWithin30Days ?? 0,
        variant: 'warning' as const,
      },
      { title: 'Expired', value: stats?.expired ?? 0, variant: 'danger' as const },
    ],
    [stats]
  );

  if (!projectId) {
    return (
      <ModuleLayout
        title="Compliance Tracking"
        description="Manage regulatory compliance across all projects"
        icon={<FileWarning className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={FileWarning}
          title="Select a project"
          description="Please select a project to view its compliance items."
        />
      </ModuleLayout>
    );
  }

  return (
    <>
      <ModuleLayout
        title="Compliance Tracking"
        description="Monitor regulatory compliance, permits, and certifications"
        icon={<FileWarning className="h-4 w-4 text-brand-600" />}
        primaryAction={{
          label: 'Add Compliance Item',
          onClick: handleAddNew,
          icon: <Plus className="h-4 w-4 mr-2" />,
        }}
        stats={statItems}
      >
        {/* ---- FILTERS ---- */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex-1 min-w-[200px] max-w-xs relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search compliance items..."
              className="pl-9"
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              options={TYPE_OPTIONS}
              value={filterType}
              onChange={val => {
                setFilterType(val);
                setPage(1);
              }}
              placeholder="All Types"
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              options={STATUS_OPTIONS}
              value={filterStatus}
              onChange={val => {
                setFilterStatus(val);
                setPage(1);
              }}
              placeholder="All Status"
            />
          </div>
          <div className="w-52">
            <SearchableDropdown
              options={EXPIRING_OPTIONS}
              value={filterExpiring}
              onChange={val => {
                setFilterExpiring(val);
                setPage(1);
              }}
              placeholder="Any Expiration"
            />
          </div>
        </div>

        {/* ---- TABLE ---- */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No compliance items found"
            description={
              filterType || filterStatus || filterExpiring || search
                ? 'No items match your current filters. Try adjusting your search.'
                : 'Start tracking regulatory compliance by adding permits, insurance certificates, and inspection records.'
            }
            action={{
              label: 'Add Compliance Item',
              onClick: handleAddNew,
            }}
          />
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className={`relative ${isFetching ? 'opacity-60' : ''}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Item
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Type
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Expiration
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Details
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Verified
                    </th>
                    <th className="w-12" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map(item => {
                    const daysLeft = getDaysUntilExpiration(item.expirationDate);
                    const isExpiringSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
                    const isExpired = daysLeft !== null && daysLeft < 0;

                    return (
                      <tr
                        key={item._id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleEdit(item)}
                      >
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{item.itemNumber}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
                            {item.itemType === 'permit' && <Building2 className="h-3 w-3" />}
                            {item.itemType === 'insurance' && <Shield className="h-3 w-3" />}
                            {item.itemType === 'inspection' && <BadgeCheck className="h-3 w-3" />}
                            {item.itemType === 'certification' && (
                              <BadgeCheck className="h-3 w-3" />
                            )}
                            {item.itemType === 'requirement' && (
                              <ShieldAlert className="h-3 w-3" />
                            )}
                            {getTypeLabel(item.itemType)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge
                            status={item.status.replace(/_/g, ' ')}
                            variant={getStatusVariant(item.status)}
                          />
                        </td>
                        <td className="py-3 px-4">
                          {item.expirationDate ? (
                            <div>
                              <div
                                className={`text-sm ${
                                  isExpired
                                    ? 'text-red-600 font-medium'
                                    : isExpiringSoon
                                    ? 'text-amber-600 font-medium'
                                    : 'text-gray-900'
                                }`}
                              >
                                {formatDate(item.expirationDate)}
                              </div>
                              {daysLeft !== null && (
                                <div
                                  className={`text-xs mt-0.5 ${
                                    isExpired
                                      ? 'text-red-500'
                                      : isExpiringSoon
                                      ? 'text-amber-500'
                                      : 'text-gray-400'
                                  }`}
                                >
                                  {isExpired
                                    ? `Expired ${Math.abs(daysLeft)}d ago`
                                    : `${daysLeft}d remaining`}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          <div className="space-y-0.5">
                            {item.permitNumber && (
                              <div className="text-xs">
                                <span className="text-gray-400">Permit:</span> {item.permitNumber}
                              </div>
                            )}
                            {item.certificateNumber && (
                              <div className="text-xs">
                                <span className="text-gray-400">Cert:</span>{' '}
                                {item.certificateNumber}
                              </div>
                            )}
                            {item.issuingAuthority && (
                              <div className="text-xs">
                                <span className="text-gray-400">Authority:</span>{' '}
                                {item.issuingAuthority}
                              </div>
                            )}
                            {item.insurer && (
                              <div className="text-xs">
                                <span className="text-gray-400">Insurer:</span> {item.insurer}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {item.verifiedAt ? (
                            <div className="flex items-center gap-1 text-xs text-green-600">
                              <BadgeCheck className="h-3.5 w-3.5" />
                              <span>{formatDate(item.verifiedAt)}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Not verified</span>
                          )}
                        </td>
                        <td
                          className="py-3 px-4"
                          onClick={e => e.stopPropagation()}
                        >
                          <ActionMenu id={`compliance-action-${item._id}`} items={getActionItems(item)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ---- PAGINATION ---- */}
            {pagination && pagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Showing {(pagination.page - 1) * pagination.limit + 1}–
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} items
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => p - 1)}
                    disabled={!pagination.hasPrev}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!pagination.hasNext}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </ModuleLayout>

      {/* ---- DRAWER ---- */}
      <ComplianceDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        projectId={projectId}
        item={editingItem}
      />

      {/* ---- VERIFY MODAL ---- */}
      {verifyItem && (
        <VerifyModal
          item={verifyItem}
          onConfirm={handleVerify}
          onCancel={() => setVerifyItem(null)}
          isLoading={isVerifying}
        />
      )}
    </>
  );
};

export default ComplianceTracking;
